import { domToForeignObjectSvg } from "modern-screenshot";
import { paraWebp } from "./screenshot";

/**
 * O print onde NÃO existe captura de tela (DEV-277).
 *
 * `getDisplayMedia` é desktop-only — em celular nenhum browser expõe a tela —, então aqui
 * a imagem não é uma FOTO: é a página **redesenhada**. A lib clona o DOM, embute os
 * estilos e pinta tudo num `<canvas>` via `<foreignObject>`. Sai um WebP igual ao da
 * captura, e daí para a frente (anotar, enviar, o `screenshot` do intake) nada sabe a
 * diferença.
 *
 * **O que o re-desenho NÃO alcança**, e é honesto dizer antes de alguém estranhar o
 * resultado: iframe de outra origem, `<video>` tocando, canvas "tainted" por imagem sem
 * CORS. Essas áreas saem em branco — o clone não tem acesso aos pixels delas, e nenhuma
 * lib desta família tem. Onde a tela é DOM + CSS + imagem da mesma origem (que é o caso
 * dos apps que embarcam o relator), o resultado é fiel.
 *
 * Recorta o VIEWPORT, não a página inteira: o desktop captura o que está na tela, e o
 * relato é sobre o que a pessoa está vendo. A página inteira ainda geraria uma imagem
 * gigante num aparelho fraco, para mostrar o que ninguém olhou.
 */
/**
 * Teto de espera por recurso da página. **O padrão da lib é 30 s, e é o que fazia a
 * geração levar mais de meio minuto** — não no aparelho fraco, e sim em qualquer um.
 *
 * Antes de desenhar, a lib espera CADA `<img>` e `<video>` do documento inteiro carregar.
 * Duas coisas comuníssimas nunca disparam `load` nem `error`: imagem com `loading="lazy"`
 * fora da tela (o browser sequer começa a buscá-la) e `<video preload="none"`. Uma só delas
 * segura tudo até o timeout. Medido no front, mesma página: `wait until load` = 0,24 ms sem
 * nenhuma travada, e exatamente o valor do timeout com UMA imagem lazy fora da tela.
 *
 * 2 s porque o que não carregou até aqui não entraria no print de qualquer jeito: a espera
 * a mais não melhora a imagem, só adia a tela. E são justamente as imagens FORA da tela que
 * travam — as que este recorte nem desenha.
 */
const ESPERA_MAXIMA_MS = 2000;

/**
 * A espera quando NADA na tela está carregando — o caso normal, e por isso o que decide a
 * sensação de rapidez. Não é zero porque a lib usa o mesmo teto para buscar as fontes e as
 * imagens de fundo que precisa embutir; é curto porque, se o que está à vista já carregou,
 * não há o que aguardar.
 */
const ESPERA_MINIMA_MS = 150;

/**
 * Quanto esperar por recurso ANTES de desenhar — decidido pelo que está NA TELA.
 *
 * O teto de 2 s existe para a imagem que a pessoa está vendo aparecer meio carregada. Só
 * que pagá-lo sempre é pagar pelo caso raro: quem abre o relator está olhando uma tela
 * pronta, onde toda imagem visível já tem `complete === true` e a lib resolve na hora. Quem
 * segura o relógio é sempre a imagem de FORA da tela (a `loading="lazy"`, que o browser nem
 * começou a buscar), e ela não entra neste recorte.
 *
 * Então a pergunta certa não é "quanto esperar", é "há algo VISÍVEL ainda carregando?".
 */
function esperaPorRecursos(): number {
  const visivel = (el: Element): boolean => {
    const r = el.getBoundingClientRect();

    return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  };
  const imagemPendente = [...document.querySelectorAll("img")].some((i) => !i.complete && visivel(i));
  const videoPendente = [...document.querySelectorAll("video")].some((v) => v.readyState < 2 && visivel(v));

  return imagemPendente || videoPendente ? ESPERA_MAXIMA_MS : ESPERA_MINIMA_MS;
}

/**
 * Teto de densidade. O aparelho pede 3x e a diferença no TEMPO é ruído (medido: 3,9 s / 3,2 s
 * / 4,2 s para 1x, 2x e 3x), mas o peso não é — 50 KB, 118 KB e 199 KB do mesmo WebP. O print
 * é para alguém LER uma tela, não para ampliar, e o upload sai de uma conexão de celular.
 */
const DENSIDADE_MAXIMA = 2;

/**
 * Teto para a imagem final decodificar. Nada a ver com o teto de recursos da página: aqui não
 * se está esperando a REDE, e sim o browser terminar de decodificar um SVG que já está na
 * mão. Generoso porque quem o alcança é um aparelho em apuros, e o custo de errar para menos
 * é um print em branco — o defeito que este número existe para não repetir.
 */
const DECODIFICACAO_MAXIMA_MS = 15000;

export async function rasterizarPagina(maxLargura = 1920): Promise<Blob> {
  const largura = Math.max(1, window.innerWidth);
  const altura = Math.max(1, window.innerHeight);
  // A densidade do aparelho, com dois tetos: o de nitidez (acima) e o de largura da captura
  // — anexo tem limite de tamanho no backend, e o downscale é aqui porque depois do canvas
  // já não há o que reduzir sem perder nitidez.
  const escala = Math.min(window.devicePixelRatio || 1, DENSIDADE_MAXIMA, maxLargura / largura);

  // Só a PRIMEIRA metade do trabalho é da lib: clonar a página num `<svg><foreignObject>`.
  // A segunda — virar imagem e ir para o canvas — é feita aqui de propósito; o porquê está
  // em `desenhar`.
  const svg = await domToForeignObjectSvg(document.documentElement, {
    width: largura,
    height: altura,
    scale: escala,
    timeout: esperaPorRecursos(),
    // O clone nasce no topo do documento; sem desfazer o scroll, a imagem seria sempre o
    // cabeçalho da página, e não a parte que a pessoa está olhando.
    style: {
      transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
      transformOrigin: "top left",
    },
    // Imagem de outra origem sem CORS não entra no canvas de jeito nenhum — pular a
    // tentativa evita a exceção que derrubaria a captura inteira por causa de um logo.
    filter: (no) => !(no instanceof HTMLElement && no.hasAttribute("data-altbug-host")),
  });

  return await desenhar(svg, Math.floor(largura * escala), Math.floor(altura * escala));
}

/**
 * O SVG vira imagem e a imagem vira canvas — e isto NÃO é reimplementar o `domToCanvas` da
 * lib por gosto. É que ela usa **um único** `timeout` para duas esperas que não têm nada a
 * ver uma com a outra:
 *
 * - esperar os recursos DA PÁGINA (que queremos curto — ver `esperaPorRecursos`);
 * - esperar a imagem final DECODIFICAR antes do `drawImage`.
 *
 * Estourado o teto, ela desenha assim mesmo. No desktop isso nunca aparecia: o SVG decodifica
 * em poucos milissegundos. No celular — SVG maior (densidade 2x), CPU menor — ele passava dos
 * 150 ms, o `drawImage` acontecia numa imagem ainda não decodificada e o print saía EM BRANCO:
 * a moldura respondia ao toque, dava para anotar, e não havia nada embaixo das marcas.
 *
 * Aqui a espera é `decode()`, que termina exatamente quando a imagem está pronta para
 * desenhar. O teto existe só para não prender a tela para sempre, e é generoso porque
 * ninguém o alcança em condições normais.
 */
async function desenhar(svg: SVGElement, largura: number, altura: number): Promise<Blob> {
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(svg))}`;
  await prontaParaDesenhar(img, DECODIFICACAO_MAXIMA_MS);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, largura);
  canvas.height = Math.max(1, altura);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponível.");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return await paraWebp(canvas);
}

/**
 * Resolve quando a imagem está DECODIFICADA — não só baixada. `decode()` é a única promessa
 * que o browser dá sobre isso; o evento `load` dispara antes, e desenhar ali é o que produzia
 * o quadrado em branco. O `load` fica de reserva para quem não tem `decode` (Safari antigo),
 * e o teto vira ERRO, não um desenho vazio: no celular a falha revela o print do aparelho,
 * que é uma saída de verdade, enquanto uma imagem branca se parece com sucesso.
 */
function prontaParaDesenhar(img: HTMLImageElement, tetoMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const naoDeu = (): void => reject(new Error("A imagem da página não ficou pronta a tempo."));
    const timer = setTimeout(naoDeu, tetoMs);
    const fim = (fn: () => void) => (): void => {
      clearTimeout(timer);
      fn();
    };
    if (typeof img.decode === "function") {
      void img.decode().then(fim(resolve), fim(naoDeu));

      return;
    }
    img.addEventListener("load", fim(resolve), { once: true });
    img.addEventListener("error", fim(naoDeu), { once: true });
  });
}

/**
 * Este aparelho consegue rasterizar? `foreignObject` + canvas existem em tudo que nos
 * interessa, mas a checagem mora aqui para o widget nunca prometer o botão a um browser
 * que não o cumpra — é a mesma regra do `suportaCapturaTela`.
 */
export function suportaRasterizar(): boolean {
  return typeof document.createElement("canvas").getContext === "function";
}
